import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generate and download a PDF from an HTML element
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @param {string} filename - The filename for the downloaded PDF
 * @param {Object} options - Additional options for PDF generation
 */
export const generatePDFFromElement = async (element, filename = 'document.pdf', options = {}) => {
  try {
    // Default options
    const defaultOptions = {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      ...options
    };

    // Convert HTML to canvas
    const canvas = await html2canvas(element, defaultOptions);
    
    // Get canvas dimensions
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // Calculate how many pages we need
    let heightLeft = imgHeight;
    let position = 0;
    const pageCount = Math.ceil(imgHeight / pageHeight);

    // Add image to PDF
    const imgData = canvas.toDataURL('image/png', 1.0);
    
    // First page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
    heightLeft -= pageHeight;

    // Add additional pages if content is longer than one page
    let page = 1;
    while (heightLeft > 0 && page < pageCount) {
      position = -(pageHeight * page);
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;
      page++;
    }

    // Download the PDF
    pdf.save(filename);
    
    return { success: true };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate invoice PDF with proper formatting
 * @param {string} elementId - The ID of the element to convert
 * @param {string} invoiceNumber - Invoice number for filename
 */
export const downloadInvoicePDF = async (elementId, invoiceNumber = 'invoice') => {
  const element = document.getElementById(elementId);
  
  if (!element) {
    console.error('Element not found:', elementId);
    return { success: false, error: 'Element not found' };
  }

  const filename = `${invoiceNumber}.pdf`;
  
  // Optimize element for PDF generation
  const originalPadding = element.style.padding;
  const originalMargin = element.style.margin;
  
  // Temporarily adjust styling for better PDF output
  element.style.padding = '20px';
  element.style.margin = '0';
  
  const result = await generatePDFFromElement(element, filename, {
    scale: 3, // Higher quality for invoices
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: 1200, // Fixed width for consistent rendering
  });
  
  // Restore original styling
  element.style.padding = originalPadding;
  element.style.margin = originalMargin;
  
  return result;
};
